import { getCurrencyByCode } from '@/constants/currencies'

/**
 * Money formatting. App-wide, every amount is an integer in **hundredths**,
 * independent of a currency's actual fraction digits — so display precision
 * comes from the currency, not a hard-coded `.toFixed(2)`.
 */

/** Decimal-string amount from a form input (e.g. "12.50") to integer cents. */
export const toCents = (amount: string): number => Math.round(parseFloat(amount || '0') * 100)

const decimalsCache = new Map<string, number>()

/** ISO 4217 fraction digits via `Intl`, falling back to 2. Memoized. */
export const getCurrencyDecimals = (currencyCode: string): number => {
	const key = currencyCode.toLowerCase()
	const cached = decimalsCache.get(key)
	if (cached !== undefined) return cached
	let decimals = 2
	try {
		decimals =
			new Intl.NumberFormat('en', {
				style: 'currency',
				currency: currencyCode.toUpperCase(),
			}).resolvedOptions().maximumFractionDigits ?? 2
	} catch {
		decimals = 2
	}
	decimalsCache.set(key, decimals)
	return decimals
}

/**
 * Fraction digits to *display*. Storage is 2-decimal, so currencies with more
 * minor-unit digits (BHD/KWD → 3) clamp down rather than render a phantom zero.
 */
export const getDisplayDecimals = (currencyCode: string): number =>
	Math.min(getCurrencyDecimals(currencyCode), 2)

/**
 * Integer cents to an input-field string at the currency's display precision
 * (e.g. 1250, 'usd' -> "12.50"; 10000, 'jpy' -> "100"). Defined below
 * `getDisplayDecimals`, which it delegates precision to.
 */
export const fromCents = (cents: number, currencyCode: string): string =>
	(cents / 100).toFixed(getDisplayDecimals(currencyCode))

/**
 * Prefill strings for a set of stored shares that must keep adding up to their
 * own total once rounded to the currency's display precision.
 *
 * Formatting each share on its own drops the rounding remainder — three JPY
 * shares of 3333/3333/3334 each render "33" and the reopened form allocates ¥99
 * of ¥100. So each share floors to the display precision and the remainder is
 * handed back a step at a time, largest fractional part first: "33"/"33"/"34".
 * Mirrors how the split itself rounds (CONTEXT.md, Expense) — floor everyone,
 * then place what's left over.
 *
 * @param shares - Stored shares in integer cents
 * @param currencyCode - Whose display precision the strings round to
 * @returns One input-field string per share, in the order given
 */
export const splitPrefill = (shares: readonly number[], currencyCode: string): string[] => {
	const decimals = getDisplayDecimals(currencyCode)
	// Cents per displayable step: 1 at 2 decimals, 10 at 1, 100 at 0.
	const step = 100 / 10 ** decimals

	const floored = shares.map(share => Math.floor(share / step))
	const total = shares.reduce((sum, share) => sum + share, 0)
	// The remainder is whatever the displayed total has that the floors don't.
	let remaining = Math.round(total / step) - floored.reduce((sum, steps) => sum + steps, 0)

	const byLargestFraction = shares
		.map((share, index) => ({ index, fraction: share / step - floored[index] }))
		.sort((a, b) => b.fraction - a.fraction || a.index - b.index)

	for (const { index } of byLargestFraction) {
		if (remaining <= 0) break
		floored[index] += 1
		remaining -= 1
	}

	return floored.map(steps => ((steps * step) / 100).toFixed(decimals))
}

/**
 * Bare decimal string — no symbol or separators — for machine-readable output
 * (CSV). Sign is preserved (`-3333, 'usd'` → "-33.33").
 */
export const formatMoneyPlain = (amount: number, currencyCode: string): string =>
	fromCents(amount, currencyCode)

/**
 * The symbol to prefix an amount input with. Unknown codes fall back to `$` —
 * the inputs that use it are always paired with an explicit currency selector,
 * so the fallback labels the field rather than naming a currency.
 */
export const currencySymbol = (currencyCode: string): string =>
	getCurrencyByCode(currencyCode)?.symbol ?? '$'

/**
 * Format a stored amount with its currency symbol. Unknown codes fall back to a
 * code suffix ("42.00 ZZZ") rather than a `$` that could misstate the currency.
 * Caller controls sign. e.g. (4200, 'usd') → "$42.00"; (10000, 'jpy') → "¥100".
 */
export const formatMoney = (amount: number, currencyCode: string): string => {
	const currency = getCurrencyByCode(currencyCode)
	const value = fromCents(amount, currencyCode)
	if (!currency) return currencyCode ? `${value} ${currencyCode.toUpperCase()}` : value
	return currency.isSymbolOnLeft ? `${currency.symbol}${value}` : `${value}${currency.symbol}`
}
